import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

const Privacy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-4xl mx-auto px-8 py-12">
        <Button
          onClick={() => navigate('/')}
          variant="ghost"
          className="mb-8 text-gray-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
        <p className="text-gray-400 mb-8">Last updated: November 8, 2025</p>

        <div className="space-y-8 text-gray-300">
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. Introduction</h2>
            <p>
              Welcome to PropFlo ("we," "our," or "us"). We are committed to protecting your personal information 
              and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard 
              your information when you use our AI-powered real estate lead qualification platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. Information We Collect</h2>
            <p className="mb-4">We collect information that you provide directly to us, including:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Personal Information:</strong> Name, email address, phone number, and other contact details</li>
              <li><strong>Real Estate Preferences:</strong> Budget, location preferences, property requirements, timeline</li>
              <li><strong>Communication Data:</strong> Conversation transcripts from voice calls, SMS messages, and emails</li>
              <li><strong>Account Information:</strong> Login credentials, account settings, and preferences</li>
              <li><strong>Usage Information:</strong> How you interact with our platform, features used, and timestamps</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. How We Use Your Information</h2>
            <p className="mb-4">We use the collected information for the following purposes:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>To provide and maintain our AI-powered lead qualification services</li>
              <li>To communicate with you about properties, market updates, and appointments</li>
              <li>To improve our AI algorithms and service quality</li>
              <li>To sync your data with Follow Up Boss CRM for better lead management</li>
              <li>To send you marketing communications (with your consent)</li>
              <li>To comply with legal obligations and protect our rights</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. Information Sharing and Disclosure</h2>
            <p className="mb-4">We may share your information with:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Real Estate Agents:</strong> Licensed realtors working with PropFlo to serve you</li>
              <li><strong>Service Providers:</strong> Third-party services including Retell AI, OpenAI, Twilio, and Follow Up Boss</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our legal rights</li>
              <li><strong>Business Transfers:</strong> In connection with any merger, sale, or acquisition</li>
            </ul>
            <p className="mt-4">
              We do NOT sell your personal information to third parties for marketing purposes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. Data Security</h2>
            <p>
              We implement appropriate technical and organizational security measures to protect your personal information. 
              This includes encryption, secure data storage, and regular security audits. However, no method of transmission 
              over the internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Your Privacy Rights</h2>
            <p className="mb-4">Depending on your location, you may have the following rights:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Access:</strong> Request access to your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications</li>
              <li><strong>Data Portability:</strong> Request a copy of your data in a portable format</li>
            </ul>
            <p className="mt-4">
              To exercise these rights, please contact us at <a href="mailto:support@aipropflo.com" className="text-blue-400 hover:text-blue-300">support@aipropflo.com</a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. AI and Automated Decision-Making</h2>
            <p>
              PropFlo uses artificial intelligence to score and qualify leads. Our AI analyzes conversation data, 
              budget information, and behavioral signals to categorize leads as HOT, WARM, or COLD. These scores 
              are used to prioritize follow-ups and optimize agent productivity. You have the right to request 
              human review of any automated decision that significantly affects you.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Cookies and Tracking Technologies</h2>
            <p>
              We use cookies and similar tracking technologies to track activity on our platform and store certain information. 
              You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Children's Privacy</h2>
            <p>
              Our services are not intended for individuals under the age of 18. We do not knowingly collect personal 
              information from children. If you are a parent or guardian and believe your child has provided us with 
              personal information, please contact us.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of residence. 
              We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new 
              Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy 
              Policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4">12. Contact Us</h2>
            <p className="mb-4">
              If you have any questions about this Privacy Policy, please contact us:
            </p>
            <ul className="list-none space-y-2 ml-4">
              <li><strong>Email:</strong> <a href="mailto:support@aipropflo.com" className="text-blue-400 hover:text-blue-300">support@aipropflo.com</a></li>
              <li><strong>Website:</strong> <a href="https://www.aipropflo.com" className="text-blue-400 hover:text-blue-300">www.aipropflo.com</a></li>
              <li><strong>Address:</strong> Saint John, NB, Canada</li>
            </ul>
          </section>

          <div className="mt-12 p-6 bg-blue-900/20 border border-blue-800 rounded-lg">
            <p className="text-sm text-gray-400">
              <strong className="text-white">Note:</strong> This privacy policy applies to PropFlo's AI-powered lead qualification platform. 
              For questions about real estate services, please contact your assigned licensed REALTOR®.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
